import {
  useCreatePostalCode,
  useListPostalCodes,
  useDeletePostalCode,
  useCreateContractReference,
  useListContractReferences,
  useUpdateContractReference,
  useDeleteContractReference,
} from '@/api/access-control/access-control';
import type { ContractReferenceResponse } from '@/api/model';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getApiErrorMessage } from '@/lib/api-error';
import { convertDateLocalToISO } from '@/lib/utils';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { MapPin, FileText, Plus, Trash2, RefreshCw, Pencil, AlertTriangle } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { VoucherPagination } from '@/components/vouchers/VoucherPagination';

const ITEMS_PER_PAGE = 10;

function validityYearsLabel(years: number | string | null | undefined): string {
  return Number(years) >= 2 ? '2 Jahre' : '1 Jahr';
}

function expiresAtFromYears(yearsValue: string): string {
  const years = parseInt(yearsValue, 10);
  const local = new Date();
  local.setFullYear(local.getFullYear() + years);
  const yyyy = local.getFullYear();
  const mm = String(local.getMonth() + 1).padStart(2, '0');
  const dd = String(local.getDate()).padStart(2, '0');
  // End-of-day local → UTC ISO (shared helper, matches backend UTC contract).
  return convertDateLocalToISO(`${yyyy}-${mm}-${dd}`);
}

function clampCreditAmountInput(value: string): string {
  if (value === '' || value === '.') return value;

  const num = parseFloat(value);
  const [, decimals] = value.split('.');
  if (!isNaN(num) && num >= 0 && decimals !== undefined && decimals.length > 2) return num.toFixed(2);

  return value;
}

export const AccessControlPage: React.FC = () => {
  const [postalCodeInput, setPostalCodeInput] = useState('');
  const [contractRefInput, setContractRefInput] = useState('');
  const [contractRefProductId, setContractRefProductId] = useState('');
  const [contractRefNote, setContractRefNote] = useState('');
  const [contractRefExpiresInYears, setContractRefExpiresInYears] = useState<string>('1');
  const [contractRefCreditAmount, setContractRefCreditAmount] = useState<string>('200.00');
  const [deletePostalCodeId, setDeletePostalCodeId] = useState<string | null>(null);
  const [deleteContractRefId, setDeleteContractRefId] = useState<string | null>(null);
  const [editingContractRef, setEditingContractRef] = useState<ContractReferenceResponse | null>(null);
  const [editProductId, setEditProductId] = useState('');
  const [editContractName, setEditContractName] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editExpiresInYears, setEditExpiresInYears] = useState('1');
  const [editChangeValidity, setEditChangeValidity] = useState(false);
  const [editCreditAmount, setEditCreditAmount] = useState('200.00');
  const [postalCodePage, setPostalCodePage] = useState<number>(1);
  const [contractRefPage, setContractRefPage] = useState<number>(1);

  // API hooks
  const {
    data: postalCodesData,
    refetch: refetchPostalCodes,
    isLoading: isLoadingPostalCodes,
    isError: isErrorPostalCodes,
    error: errorPostalCodes,
  } = useListPostalCodes({ limit: 1000, offset: 0 }, { query: { enabled: true } });

  const {
    data: contractRefsData,
    refetch: refetchContractRefs,
    isLoading: isLoadingContractRefs,
    isError: isErrorContractRefs,
    error: errorContractRefs,
  } = useListContractReferences({ limit: 1000, offset: 0 }, { query: { enabled: true } });

  const createPostalCodeMutation = useCreatePostalCode();
  const deletePostalCodeMutation = useDeletePostalCode();
  const createContractRefMutation = useCreateContractReference();
  const updateContractRefMutation = useUpdateContractReference();
  const deleteContractRefMutation = useDeleteContractReference();

  const allPostalCodes = React.useMemo(
    () => postalCodesData?.postal_codes ?? [],
    [postalCodesData?.postal_codes]
  );
  const allContractRefs = React.useMemo(
    () => contractRefsData?.contract_references ?? [],
    [contractRefsData?.contract_references]
  );
  const contractRefsMissingProductId = React.useMemo(
    () => allContractRefs.filter(cr => !cr.product_id?.trim()),
    [allContractRefs]
  );

  // Client-side pagination for postal codes
  const postalCodeTotalCount = allPostalCodes.length;
  const postalCodeTotalPages = Math.max(1, Math.ceil(postalCodeTotalCount / ITEMS_PER_PAGE));
  const paginatedPostalCodes = React.useMemo(() => {
    if (allPostalCodes.length === 0) return [];
    const startIndex = (postalCodePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return allPostalCodes.slice(startIndex, endIndex);
  }, [allPostalCodes, postalCodePage]);

  // Client-side pagination for contract references
  const contractRefTotalCount = allContractRefs.length;
  const contractRefTotalPages = Math.max(1, Math.ceil(contractRefTotalCount / ITEMS_PER_PAGE));
  const paginatedContractRefs = React.useMemo(() => {
    if (allContractRefs.length === 0) return [];
    const startIndex = (contractRefPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return allContractRefs.slice(startIndex, endIndex);
  }, [allContractRefs, contractRefPage]);

  React.useEffect(() => {
    if (postalCodePage > postalCodeTotalPages && postalCodeTotalPages > 0) {
      setPostalCodePage(postalCodeTotalPages);
    }
  }, [postalCodePage, postalCodeTotalPages]);

  React.useEffect(() => {
    if (contractRefPage > contractRefTotalPages && contractRefTotalPages > 0) {
      setContractRefPage(contractRefTotalPages);
    }
  }, [contractRefPage, contractRefTotalPages]);

  const isLoading =
    isLoadingPostalCodes ||
    isLoadingContractRefs ||
    createPostalCodeMutation.isPending ||
    deletePostalCodeMutation.isPending ||
    createContractRefMutation.isPending ||
    updateContractRefMutation.isPending ||
    deleteContractRefMutation.isPending;

  const handleAddPostalCode = async () => {
    // Split by comma and trim each postal code
    const postalCodes = postalCodeInput
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);

    if (postalCodes.length === 0) {
      toast.error('Bitte geben Sie mindestens eine Postleitzahl ein');
      return;
    }

    // Validate all postal codes are 5 digits
    const invalidCodes = postalCodes.filter(code => !code.match(/^[0-9]{5}$/));
    if (invalidCodes.length > 0) {
      toast.error(`Ungültige Postleitzahlen: ${invalidCodes.join(', ')}. Bitte geben Sie nur 5-stellige Zahlen ein.`);
      return;
    }

    // Create all postal codes sequentially to avoid race conditions
    let successful = 0;
    let failed = 0;
    const failedCodes: string[] = [];

    for (const postalCode of postalCodes) {
      try {
        await createPostalCodeMutation.mutateAsync({
          data: { postal_code: postalCode },
        });
        successful++;
      } catch (_error) {
        failed++;
        failedCodes.push(postalCode);
        // Don't show individual errors, we'll show a summary
      }
    }

    if (successful > 0) {
      toast.success(`${successful} Postleitzahl${successful > 1 ? 'en' : ''} erfolgreich hinzugefügt`);
    }
    if (failed > 0) {
      toast.error(
        `${failed} Postleitzahl${failed > 1 ? 'en' : ''} konnten nicht hinzugefügt werden${
          failedCodes.length > 0 ? `: ${failedCodes.join(', ')}` : ''
        } (möglicherweise bereits vorhanden)`
      );
    }

    setPostalCodeInput('');
    await refetchPostalCodes();
  };

  const handleAddContractReference = async () => {
    if (!contractRefProductId.trim()) {
      toast.error('Bitte geben Sie eine Produkt-ID ein');
      return;
    }

    if (!contractRefInput.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    // Validate credit amount (max 2 decimals)
    const creditAmount = Math.round(parseFloat(contractRefCreditAmount) * 100) / 100;
    if (isNaN(creditAmount) || creditAmount < 0) {
      toast.error('Bitte geben Sie einen gültigen Kreditbetrag ein (≥ 0)');
      return;
    }

    try {
      await createContractRefMutation.mutateAsync({
        data: {
          product_id: contractRefProductId.trim(),
          contract_name: contractRefInput.trim(),
          note: contractRefNote.trim() || null,
          expires_at: expiresAtFromYears(contractRefExpiresInYears),
          validity_years: parseInt(contractRefExpiresInYears, 10),
          credit_amount: creditAmount,
        },
      });
      toast.success(`Tarifreferenz ${contractRefInput} hinzugefügt`);
      setContractRefProductId('');
      setContractRefInput('');
      setContractRefNote('');
      setContractRefExpiresInYears('1');
      setContractRefCreditAmount('200.00');
      await refetchContractRefs();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Hinzufügen der Tarifreferenz'));
    }
  };

  const handleDeletePostalCode = async () => {
    if (!deletePostalCodeId) return;

    try {
      await deletePostalCodeMutation.mutateAsync({
        postalCodeId: deletePostalCodeId,
      });
      toast.success('Postleitzahl gelöscht');
      setDeletePostalCodeId(null);
      await refetchPostalCodes();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Löschen der Postleitzahl'));
    }
  };

  const openEditContractReference = (cr: ContractReferenceResponse) => {
    setEditingContractRef(cr);
    setEditProductId(cr.product_id ?? '');
    setEditContractName(cr.contract_name);
    setEditNote(cr.note ?? '');
    const expiresYears = Number(cr.validity_years) >= 2 ? '2' : '1';
    setEditExpiresInYears(expiresYears);
    setEditChangeValidity(false);
    setEditCreditAmount(cr.credit_amount !== undefined && cr.credit_amount !== null
      ? parseFloat(cr.credit_amount.toString()).toFixed(2)
      : '200.00'
    );
  };

  const closeEditContractReference = () => {
    setEditingContractRef(null);
  };

  const handleUpdateContractReference = async () => {
    if (!editingContractRef) return;

    if (!editProductId.trim()) {
      toast.error('Bitte geben Sie eine Produkt-ID ein');
      return;
    }
    if (!editContractName.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    const creditAmount = Math.round(parseFloat(editCreditAmount) * 100) / 100;
    if (isNaN(creditAmount) || creditAmount < 0) {
      toast.error('Bitte geben Sie einen gültigen Kreditbetrag ein (≥ 0)');
      return;
    }

    const validityYears = editChangeValidity
      ? parseInt(editExpiresInYears, 10)
      : Number(editingContractRef.validity_years) >= 2
        ? 2
        : 1;
    const expiresAt = editChangeValidity
      ? expiresAtFromYears(editExpiresInYears)
      : (editingContractRef.expires_at ?? null);

    try {
      await updateContractRefMutation.mutateAsync({
        contractRefId: editingContractRef.id,
        data: {
          product_id: editProductId.trim(),
          contract_name: editContractName.trim(),
          note: editNote.trim() || null,
          expires_at: expiresAt,
          validity_years: validityYears,
          credit_amount: creditAmount,
        },
      });
      toast.success(`Tarifreferenz ${editContractName} aktualisiert`);
      closeEditContractReference();
      await refetchContractRefs();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Aktualisieren der Tarifreferenz'));
    }
  };

  const handleDeleteContractReference = async () => {
    if (!deleteContractRefId) return;

    try {
      await deleteContractRefMutation.mutateAsync({
        contractRefId: deleteContractRefId,
      });
      toast.success('Tarifreferenz gelöscht');
      setDeleteContractRefId(null);
      await refetchContractRefs();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Löschen der Tarifreferenz'));
    }
  };

  return (
    <div className='space-y-6'>
      {/* Page header */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Zugriffskontrolle</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Verwaltung von gültigen Postleitzahlen und Tarifreferenzen
          </CardDescription>
        </div>
      </section>

      {/* Tabs for Postal Codes and Contract References */}
      <Tabs defaultValue='postal-codes' className='w-full'>
        <TabsList className='grid w-full max-w-md grid-cols-2'>
          <TabsTrigger value='postal-codes'>
            <MapPin className='h-4 w-4 mr-2' />
            Postleitzahlen
          </TabsTrigger>
          <TabsTrigger value='contract-references'>
            <FileText className='h-4 w-4 mr-2' />
            Tarifreferenzen
          </TabsTrigger>
        </TabsList>

        {/* Postal Codes Tab */}
        <TabsContent value='postal-codes' className='space-y-4'>
          {/* Add Postal Code Form */}
          <section className='rounded-xl border border-neutral-hellgrau bg-white px-4 py-4'>
            <div className='flex items-start gap-3'>
              <div className='flex-1'>
                <Label htmlFor='postalCode'>Postleitzahlen (5-stellig, durch Komma getrennt)</Label>
                <Input
                  id='postalCode'
                  type='text'
                  placeholder='z.B. 12345,67890,11111'
                  value={postalCodeInput}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9,]/g, ''); // Remove non-digits and non-commas

                    // Split by comma and process each block
                    const parts = value.split(',');
                    const processedParts: string[] = [];

                    for (let i = 0; i < parts.length; i++) {
                      const part = parts[i];
                      // Only allow up to 5 digits per block
                      if (part.length <= 5) {
                        processedParts.push(part);
                      } else {
                        // If more than 5 digits, split into 5-digit blocks
                        const blocks = part.match(/.{1,5}/g) || [];
                        processedParts.push(...blocks);
                      }
                    }

                    // Join with commas, but don't add comma after the last block if it's incomplete
                    const result = processedParts.join(',');
                    setPostalCodeInput(result);
                  }}
                  className='mt-2'
                />
                <p className='text-xs text-muted-foreground mt-1'>
                  Mehrere Postleitzahlen können durch Komma getrennt eingegeben werden
                </p>
              </div>
              <Button
                onClick={handleAddPostalCode}
                disabled={
                  isLoading ||
                  !postalCodeInput
                    .split(',')
                    .map(c => c.trim())
                    .some(code => code.match(/^[0-9]{5}$/))
                }
                variant='enabled'
                size='sm'
                className='mt-7'
              >
                <Plus className='h-4 w-4 mr-2' />
                Hinzufügen
              </Button>
            </div>
          </section>

          {/* Postal Codes Table */}
          <section className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
            {isErrorPostalCodes ? (
              <div className='text-center py-8 bg-white'>
                <Alert variant='destructive' className='mx-4 my-4'>
                  <AlertDescription>
                    Fehler beim Laden der Postleitzahlen: {getApiErrorMessage(errorPostalCodes, 'Unbekannter Fehler')}
                  </AlertDescription>
                </Alert>
              </div>
            ) : isLoadingPostalCodes ? (
              <div className='text-center py-12 bg-white'>
                <RefreshCw className='h-8 w-8 text-swd-tuerkis mx-auto mb-4 animate-spin' />
                <p className='text-muted-foreground mb-4'>Lade Postleitzahlen...</p>
              </div>
            ) : allPostalCodes.length === 0 ? (
              <div className='text-center py-8 bg-white'>
                <MapPin className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
                <p className='text-muted-foreground mb-4'>Keine Postleitzahlen hinterlegt</p>
              </div>
            ) : (
              <>
                <Table className='min-w-150'>
                  <TableHeader className='bg-swd-hellblau'>
                    <TableRow>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Postleitzahl
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Erstellt am
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide w-25 text-center whitespace-nowrap'>
                        Aktionen
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedPostalCodes.map(pc => (
                      <TableRow key={pc.id} className='hover:bg-swd-hellblau/40 border-b border-neutral-hellgrau'>
                        <TableCell className='font-medium text-neutral-dunkelgrau align-middle text-sm'>
                          {pc.postal_code}
                        </TableCell>
                        <TableCell className='align-middle font-medium text-neutral-grau text-sm'>
                          {new Date(pc.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className='text-center align-middle'>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8 w-8 p-0'
                                onClick={() => setDeletePostalCodeId(pc.id)}
                              >
                                <Trash2 className='h-4 w-4 text-red-500' />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Postleitzahl löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Möchten Sie die Postleitzahl <strong>{pc.postal_code}</strong> wirklich löschen? Diese
                                  Aktion kann nicht rückgängig gemacht werden.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setDeletePostalCodeId(null)}>
                                  Abbrechen
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={handleDeletePostalCode}
                                  disabled={isLoading}
                                  variant='error'
                                >
                                  {isLoading && <RefreshCw className='h-4 w-4 animate-spin mr-2' />}
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {postalCodeTotalPages > 1 && (
                  <div className='border-t border-neutral-hellgrau bg-white px-6 py-4'>
                    <VoucherPagination
                      currentPage={postalCodePage}
                      totalPages={postalCodeTotalPages}
                      totalVouchers={postalCodeTotalCount}
                      onPageChange={setPostalCodePage}
                      itemLabel='PLZ'
                    />
                  </div>
                )}
              </>
            )}
          </section>
        </TabsContent>

        {/* Tarifreferenzen Tab */}
        <TabsContent value='contract-references' className='space-y-4'>
          {/* Add Tarifreferenz Form */}
          <section className='rounded-xl border border-neutral-hellgrau bg-white px-4 py-4'>
            <div className='space-y-4'>
              <div>
                <Label htmlFor='contractRefProductId'>Produkt-ID</Label>
                <Input
                  id='contractRefProductId'
                  type='text'
                  placeholder='z.B. 12345'
                  value={contractRefProductId}
                  onChange={e => setContractRefProductId(e.target.value)}
                  maxLength={255}
                  className='mt-2'
                />
              </div>
              <div>
                <Label htmlFor='contractRef'>Name</Label>
                <Input
                  id='contractRef'
                  type='text'
                  placeholder='z.B. SWD-KlassikStrom Grundversorgung'
                  value={contractRefInput}
                  onChange={e => setContractRefInput(e.target.value)}
                  maxLength={255}
                  className='mt-2'
                />
              </div>
              <div>
                <Label htmlFor='contractRefNote'>Notiz (optional)</Label>
                <Input
                  id='contractRefNote'
                  type='text'
                  placeholder='Optionale Notiz oder Beschreibung'
                  value={contractRefNote}
                  onChange={e => setContractRefNote(e.target.value)}
                  maxLength={500}
                  className='mt-2'
                />
              </div>
              <div>
                <Label htmlFor='contractRefExpiresIn'>Gültigkeitsdauer</Label>
                <Select value={contractRefExpiresInYears} onValueChange={setContractRefExpiresInYears}>
                  <SelectTrigger id='contractRefExpiresIn' className='mt-2'>
                    <SelectValue placeholder='Gültigkeitsdauer wählen' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='1'>1 Jahr</SelectItem>
                    <SelectItem value='2'>2 Jahre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor='contractRefCreditAmount'>Kreditbetrag (€)</Label>
                <Input
                  id='contractRefCreditAmount'
                  type='number'
                  step='0.01'
                  min='0'
                  placeholder='200.00'
                  value={contractRefCreditAmount}
                  onChange={e => setContractRefCreditAmount(clampCreditAmountInput(e.target.value))}
                  className='mt-2'
                />
                <p className='text-xs text-muted-foreground mt-1'>
                  Kreditbetrag, der diesem Tarif zugewiesen wird (Standard: 200.00 €)
                </p>
              </div>
              <div className='flex justify-end'>
                <Button
                  onClick={handleAddContractReference}
                  disabled={isLoading || !contractRefProductId.trim() || !contractRefInput.trim()}
                  variant='enabled'
                  size='sm'
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Hinzufügen
                </Button>
              </div>
            </div>
          </section>

          {/* Tarifreferenzen Table */}
          <section className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
            {contractRefsMissingProductId.length > 0 && !isLoadingContractRefs && !isErrorContractRefs && (
              <Alert variant='destructive' className='m-4'>
                <AlertTriangle />
                <AlertTitle>Produkt-ID fehlt bei {contractRefsMissingProductId.length} Tarifreferenz(en)</AlertTitle>
                <AlertDescription>
                  Ohne Produkt-ID greift nur der Namens-Fallback (Tarifname). Bitte die fehlenden Produkt-IDs
                  per Bearbeiten setzen, sonst bleibt der Abgleich unzuverlässig und kann Nutzer ausschließen.
                </AlertDescription>
              </Alert>
            )}
            {isErrorContractRefs ? (
              <div className='text-center py-8 bg-white'>
                <Alert variant='destructive' className='mx-4 my-4'>
                  <AlertDescription>
                    Fehler beim Laden der Tarifreferenzen: {getApiErrorMessage(errorContractRefs, 'Unbekannter Fehler')}
                  </AlertDescription>
                </Alert>
              </div>
            ) : isLoadingContractRefs ? (
              <div className='text-center py-12 bg-white'>
                <RefreshCw className='h-8 w-8 text-swd-tuerkis mx-auto mb-4 animate-spin' />
                <p className='text-muted-foreground mb-4'>Lade Tarifreferenzen...</p>
              </div>
            ) : allContractRefs.length === 0 ? (
              <div className='text-center py-8 bg-white'>
                <FileText className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
                <p className='text-muted-foreground mb-4'>Keine Tarifreferenzen hinterlegt</p>
              </div>
            ) : (
              <>
                <Table className='min-w-250'>
                  <TableHeader className='bg-swd-hellblau'>
                    <TableRow>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Produkt-ID
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Name
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Notiz
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Kreditbetrag
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Gültigkeit
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                        Erstellt am
                      </TableHead>
                      <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide w-30 text-center whitespace-nowrap'>
                        Aktionen
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedContractRefs.map(cr => (
                      <TableRow key={cr.id} className='hover:bg-swd-hellblau/40 border-b border-neutral-hellgrau'>
                        <TableCell className='font-medium text-neutral-dunkelgrau align-middle text-sm'>
                          {cr.product_id?.trim() ? (
                            cr.product_id
                          ) : (
                            <span className='text-destructive italic font-medium'>Nicht gesetzt</span>
                          )}
                        </TableCell>
                        <TableCell className='font-medium text-neutral-dunkelgrau align-middle text-sm'>
                          {cr.contract_name}
                        </TableCell>
                        <TableCell className='align-middle text-sm'>
                          {cr.note && cr.note.trim() ? (
                            <span className='text-neutral-grau'>{cr.note}</span>
                          ) : (
                            <span className='text-muted-foreground italic'>Keine Notiz</span>
                          )}
                        </TableCell>
                        <TableCell className='align-middle font-medium text-neutral-grau text-sm'>
                          {cr.credit_amount !== undefined && cr.credit_amount !== null
                            ? `${parseFloat(cr.credit_amount.toString()).toFixed(2)} €`
                            : '200.00 €'}
                        </TableCell>
                        <TableCell className='align-middle text-sm text-neutral-grau'>
                          <span>{validityYearsLabel(cr.validity_years)}</span>
                          <span className='text-muted-foreground'> · </span>
                          {cr.expires_at ? (
                            <>
                              <span className='text-muted-foreground'>bis </span>
                              <DateTimeDisplay
                                date={cr.expires_at}
                                format='date-only'
                                className='inline text-neutral-grau'
                              />
                            </>
                          ) : (
                            <span className='text-muted-foreground italic'>kein Ablaufdatum</span>
                          )}
                        </TableCell>
                        <TableCell className='align-middle font-medium text-neutral-grau text-sm'>
                          {new Date(cr.created_at).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className='text-center align-middle'>
                          <div className='flex items-center justify-center gap-1'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-8 w-8 p-0'
                              onClick={() => openEditContractReference(cr)}
                              aria-label={`Tarifreferenz ${cr.contract_name} bearbeiten`}
                            >
                              <Pencil className='h-4 w-4 text-swd-tuerkis-darker' />
                            </Button>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  className='h-8 w-8 p-0'
                                  onClick={() => setDeleteContractRefId(cr.id)}
                                  aria-label={`Tarifreferenz ${cr.contract_name} löschen`}
                                >
                                  <Trash2 className='h-4 w-4 text-red-500' />
                                </Button>
                              </AlertDialogTrigger>

                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Tarifreferenz löschen?</AlertDialogTitle>

                                  <AlertDialogDescription>
                                    Möchten Sie die Tarifreferenz <strong>{cr.contract_name}</strong> wirklich löschen?
                                    Diese Aktion kann nicht rückgängig gemacht werden.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>

                                <AlertDialogFooter>
                                  <AlertDialogCancel onClick={() => setDeleteContractRefId(null)}>
                                    Abbrechen
                                  </AlertDialogCancel>

                                  <AlertDialogAction
                                    onClick={handleDeleteContractReference}
                                    disabled={isLoading}
                                    variant='error'
                                  >
                                    {isLoading && <RefreshCw className='h-4 w-4 animate-spin mr-2' />}
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {contractRefTotalPages > 1 && (
                  <div className='border-t border-neutral-hellgrau bg-white px-6 py-4'>
                    <VoucherPagination
                      currentPage={contractRefPage}
                      totalPages={contractRefTotalPages}
                      totalVouchers={contractRefTotalCount}
                      onPageChange={setContractRefPage}
                      itemLabel='Tarifreferenzen'
                    />
                  </div>
                )}
              </>
            )}
          </section>

          <Dialog
            open={editingContractRef !== null}
            onOpenChange={open => {
              if (!open) closeEditContractReference();
            }}
          >
            <DialogContent className='sm:max-w-lg'>
              <DialogHeader>
                <DialogTitle>Tarifreferenz bearbeiten</DialogTitle>
              </DialogHeader>

              <div className='space-y-4 py-2'>
                <div>
                  <Label htmlFor='editContractRefProductId'>Produkt-ID</Label>
                  <Input
                    id='editContractRefProductId'
                    type='text'
                    placeholder='z.B. 12345'
                    value={editProductId}
                    onChange={e => setEditProductId(e.target.value)}
                    maxLength={255}
                    className='mt-2'
                  />
                </div>

                <div>
                  <Label htmlFor='editContractRefName'>Name</Label>
                  <Input
                    id='editContractRefName'
                    type='text'
                    placeholder='z.B. SWD-KlassikStrom Grundversorgung'
                    value={editContractName}
                    onChange={e => setEditContractName(e.target.value)}
                    maxLength={255}
                    className='mt-2'
                  />
                </div>

                <div>
                  <Label htmlFor='editContractRefNote'>Notiz (optional)</Label>
                  <Input
                    id='editContractRefNote'
                    type='text'
                    placeholder='Optionale Notiz oder Beschreibung'
                    value={editNote}
                    onChange={e => setEditNote(e.target.value)}
                    maxLength={500}
                    className='mt-2'
                  />
                </div>

                <div className='rounded-lg border border-neutral-hellgrau bg-swd-hellblau/30 px-3 py-3 space-y-3'>
                  <div>
                    <p className='text-sm font-medium text-neutral-dunkelgrau'>Gültigkeit</p>
                    <p className='text-sm text-neutral-grau mt-1'>
                      Konfigurierte Dauer: {validityYearsLabel(editingContractRef?.validity_years)}
                    </p>
                    <p className='text-sm text-neutral-grau'>
                      {editingContractRef?.expires_at ? (
                        <>
                          Gültig bis{' '}
                          <DateTimeDisplay
                            date={editingContractRef.expires_at}
                            format='date-only'
                            className='inline'
                          />
                        </>
                      ) : (
                        <span className='italic text-muted-foreground'>Kein Ablaufdatum</span>
                      )}
                    </p>
                  </div>

                  <div className='flex items-start gap-2'>
                    <Checkbox
                      id='editChangeValidity'
                      checked={editChangeValidity}
                      onCheckedChange={checked => setEditChangeValidity(checked === true)}
                    />
                    <Label htmlFor='editChangeValidity' className='text-sm font-normal leading-5'>
                      Laufzeit ändern
                    </Label>
                  </div>

                  {editChangeValidity && (
                    <div>
                      <Label htmlFor='editContractRefExpiresIn'>Neue Dauer ab heute</Label>
                      <Select value={editExpiresInYears} onValueChange={setEditExpiresInYears}>
                        <SelectTrigger id='editContractRefExpiresIn' className='mt-2 bg-white'>
                          <SelectValue placeholder='Dauer wählen' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='1'>1 Jahr ab heute</SelectItem>
                          <SelectItem value='2'>2 Jahre ab heute</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className='text-xs text-muted-foreground mt-2'>
                        Setzt konfigurierte Dauer und Ablaufdatum auf heute + gewählte Jahre.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor='editContractRefCreditAmount'>Kreditbetrag (€)</Label>
                  <Input
                    id='editContractRefCreditAmount'
                    type='number'
                    step='0.01'
                    min='0'
                    placeholder='200.00'
                    value={editCreditAmount}
                    onChange={e => setEditCreditAmount(clampCreditAmountInput(e.target.value))}
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Gilt für neue Gutschriften. Bestehende Konten behalten ihren bisherigen Betrag.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={closeEditContractReference} disabled={isLoading}>
                  Abbrechen
                </Button>
                <Button
                  variant='enabled'
                  onClick={handleUpdateContractReference}
                  disabled={isLoading || !editProductId.trim() || !editContractName.trim()}
                >
                  {updateContractRefMutation.isPending && (
                    <RefreshCw className='h-4 w-4 animate-spin mr-2' />
                  )}
                  Speichern
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};
